import React, { useEffect, useState, useRef } from 'react';
import {
  Compass,
  ArrowRight,
  ArrowLeft,
  X,
  Sparkles,
  Check,
  CheckCircle2,
  HelpCircle,
} from 'lucide-react';
import { useOnboarding } from '../../context/OnboardingContext.tsx';

export const OnboardingTourModal: React.FC = () => {
  const {
    isTourActive,
    currentStepIndex,
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    skipTour,
  } = useOnboarding();

  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  // Recalculate spotlight highlight position whenever current step changes or window resizes
  useEffect(() => {
    if (!isTourActive) return;

    const updatePosition = () => {
      if (currentStep.placement === 'center' || currentStep.targetSelector === 'header') {
        setTargetRect(null);
        return;
      }

      const el = document.querySelector(currentStep.targetSelector);
      if (el) {
        // Scroll target gently into view if offscreen
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);
      } else {
        setTargetRect(null);
      }
    };

    updatePosition();
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isTourActive, currentStepIndex, currentStep]);

  if (!isTourActive || !currentStep) return null;

  const isLastStep = currentStepIndex === totalSteps - 1;

  // Calculate Popover Tooltip coordinates
  let popoverStyle: React.CSSProperties = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 60,
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  if (isMobile) {
    popoverStyle = {
      position: 'fixed',
      bottom: '20px',
      left: '16px',
      right: '16px',
      zIndex: 60,
    };
  } else if (targetRect && currentStep.placement !== 'center') {
    const margin = 16;
    if (currentStep.placement === 'bottom') {
      const top = Math.min(window.innerHeight - 300, targetRect.bottom + margin);
      const left = Math.max(16, Math.min(window.innerWidth - 380, targetRect.left - 40));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 60,
      };
    } else if (currentStep.placement === 'top') {
      const top = Math.max(16, targetRect.top - 240);
      const left = Math.max(16, Math.min(window.innerWidth - 380, targetRect.left));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 60,
      };
    } else if (currentStep.placement === 'right') {
      const top = Math.max(80, Math.min(window.innerHeight - 300, targetRect.top + 20));
      const left = Math.min(window.innerWidth - 380, targetRect.right + margin);
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 60,
      };
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden font-sans pointer-events-auto">
      {/* Dimmed backdrop with genuine transparent cutout mask so highlighted target is completely UNBLURRED and crystal clear */}
      {targetRect ? (
        <svg
          className="fixed inset-0 w-full h-full pointer-events-none transition-all duration-300"
          style={{ zIndex: 50 }}
        >
          <defs>
            <mask id="spotlight-mask">
              {/* Fill everything with white (opaque backdrop) */}
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {/* Punch out a rounded transparent rectangle exactly over the target element */}
              <rect
                x={targetRect.left - 6}
                y={targetRect.top - 6}
                width={targetRect.width + 12}
                height={targetRect.height + 12}
                rx="16"
                ry="16"
                fill="black"
              />
            </mask>
          </defs>
          {/* Masked dark backdrop with zero blur over the cutout window */}
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.65)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      ) : (
        <div className="fixed inset-0 bg-black/60 pointer-events-none transition-all duration-300" style={{ zIndex: 50 }} />
      )}

      {/* Spotlight border & electric lime ring around the crystal-clear highlighted element */}
      {targetRect && (
        <div
          className="fixed rounded-2xl pointer-events-none transition-all duration-300 ring-4 ring-brand-500 shadow-[0_0_35px_rgba(1,98,252,0.45)]"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            zIndex: 55,
          }}
        />
      )}

      {/* Floating Tutorial Popover Card */}
      <div
        style={popoverStyle}
        className="w-auto sm:w-[90vw] max-w-sm sm:max-w-md bg-[#121417] text-white rounded-3xl p-4 sm:p-6 shadow-neo-float border border-white/15 animate-scale-up"
      >
        {/* Top Header & Step Tracker */}
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
          <div className="flex items-center space-x-2">
            <span className="w-2.5 h-2.5 rounded-full bg-brand-500 animate-pulse" />
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-brand-400">
              {currentStep.badge || `Step ${currentStepIndex + 1} of ${totalSteps}`}
            </span>
          </div>

          <button
            onClick={skipTour}
            className="p-1 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            title="Skip Tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step Content */}
        <div className="space-y-2 mb-6">
          <h3 className="text-base sm:text-lg font-black text-white tracking-tight flex items-center space-x-2">
            <span>{currentStep.title}</span>
          </h3>
          <p className="text-xs sm:text-sm text-white/80 leading-relaxed font-normal">
            {currentStep.description}
          </p>

          {currentStep.actionHint && (
            <div className="mt-3 p-2.5 rounded-2xl bg-white/5 border border-white/10 flex items-center space-x-2 text-[11px] text-brand-400 font-semibold">
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span>{currentStep.actionHint}</span>
            </div>
          )}
        </div>

        {/* Step Progress Indicators & Navigation Buttons */}
        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          {/* Progress Pips */}
          <div className="flex items-center space-x-1.5">
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStepIndex
                    ? 'w-6 bg-gradient-to-r from-[#0162fc] to-[#4b2efa]'
                    : idx < currentStepIndex
                    ? 'w-2 bg-white/40'
                    : 'w-2 bg-white/15'
                }`}
              />
            ))}
          </div>

          {/* Navigation CTAs */}
          <div className="flex items-center space-x-2">
            {currentStepIndex > 0 && (
              <button
                onClick={prevStep}
                className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center space-x-1 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back</span>
              </button>
            )}

            <button
              onClick={nextStep}
              className="px-5 py-2 rounded-full bg-gradient-to-r from-[#0162fc] to-[#4b2efa] hover:opacity-95 text-white text-xs font-extrabold flex items-center space-x-1.5 shadow-pulse transition-transform hover:scale-105"
            >
              <span>{isLastStep ? 'Finish Tour' : 'Next'}</span>
              {isLastStep ? (
                <Check className="w-3.5 h-3.5 stroke-[3]" />
              ) : (
                <ArrowRight className="w-3.5 h-3.5 stroke-[3]" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
